'use client';

import {
  Globe,
  MessageCircle,
  ShieldCheck,
  Twitter,
  Github,
  Share2,
  BookOpen,
  Camera,
  Youtube,
  Linkedin,
} from 'lucide-react';

// Shared validation rules and helper (exported for token strength / scoring)
export const SOCIAL_LINK_RULES = {
  website: {
    test: (v) => !v.trim() || /^https:\/\/.+/.test(v.trim()),
    message: 'Must start with https://',
  },
  telegram: {
    test: (v) => !v.trim() || /^https:\/\/t\.me\/.+/.test(v.trim()),
    message: 'Must be https://t.me/...',
  },
  discord: {
    test: (v) => !v.trim() || /^https:\/\/(discord\.com|discord\.gg)\/.+/.test(v.trim()),
    message: 'Must be https://discord.com/... or https://discord.gg/...',
  },
  twitter: {
    test: (v) => !v.trim() || /^https:\/\/(twitter\.com|x\.com)\/.+/.test(v.trim()),
    message: 'Must be https://x.com/... or https://twitter.com/...',
  },
  github: {
    test: (v) => !v.trim() || /^https:\/\/(www\.)?github\.com\/.+/.test(v.trim()),
    message: 'Must be https://github.com/...',
  },
  reddit: {
    test: (v) => !v.trim() || /^https:\/\/(www\.)?reddit\.com\/.+/.test(v.trim()),
    message: 'Must be https://reddit.com/...',
  },
  medium: {
    test: (v) => !v.trim() || /^https:\/\/(medium\.com|.+\.medium\.com)\/.+/.test(v.trim()),
    message: 'Must be https://medium.com/...',
  },
  instagram: {
    test: (v) => !v.trim() || /^https:\/\/(www\.)?instagram\.com\/.+/.test(v.trim()),
    message: 'Must be https://instagram.com/...',
  },
  youtube: {
    test: (v) => !v.trim() || /^https:\/\/(www\.)?(youtube\.com|youtu\.be)\/.+/.test(v.trim()),
    message: 'Must be https://youtube.com/... or https://youtu.be/...',
  },
  linkedin: {
    test: (v) => !v.trim() || /^https:\/\/(www\.)?linkedin\.com\/.+/.test(v.trim()),
    message: 'Must be https://linkedin.com/...',
  },
};

export const validateSocialLink = (value, key) => {
  if (!value || !key || !SOCIAL_LINK_RULES[key]) return null;
  const rule = SOCIAL_LINK_RULES[key];
  return rule.test(value) ? null : rule.message;
};

const FIELDS = [
  { key: 'website', label: 'Website', icon: Globe, placeholder: 'https://' },
  { key: 'telegram', label: 'Telegram', icon: MessageCircle, placeholder: 'https://t.me/...' },
  { key: 'discord', label: 'Discord', icon: ShieldCheck, placeholder: 'https://discord.gg/... or https://discord.com/...' },
  { key: 'twitter', label: 'X (Twitter)', icon: Twitter, placeholder: 'https://x.com/... or https://twitter.com/...' },
  { key: 'github', label: 'GitHub', icon: Github, placeholder: 'https://github.com/...' },
  { key: 'reddit', label: 'Reddit', icon: Share2, placeholder: 'https://reddit.com/...' },
  { key: 'medium', label: 'Medium', icon: BookOpen, placeholder: 'https://medium.com/...' },
  { key: 'instagram', label: 'Instagram', icon: Camera, placeholder: 'https://instagram.com/...' },
  { key: 'youtube', label: 'YouTube', icon: Youtube, placeholder: 'https://youtube.com/... or https://youtu.be/...' },
  { key: 'linkedin', label: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/...' },
];

const inputBaseClass = 'w-full rounded-xl border px-3 py-2 sm:px-4 sm:py-3 text-white placeholder-gray-500 focus:outline-none';
const inputValidClass = 'border-gray-700 bg-gray-800/70 focus:border-cyan-400';
const inputErrorClass = 'border-amber-500/60 bg-gray-800/70';

export default function SocialLinksSection({ form, updateField }) {
  return (
    <div className="grid gap-5 sm:gap-6 sm:grid-cols-2">
      {FIELDS.map(({ key, label, icon: Icon, placeholder }) => {
        const value = form[key] ?? '';
        const error = validateSocialLink(value, key);
        return (
          <div key={key} className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <Icon className="h-4 w-4 text-cyan-300" />
              {label}
            </label>
            <input
              value={value}
              onChange={(e) => updateField(key, e.target.value)}
              placeholder={placeholder}
              className={`${inputBaseClass} ${error ? inputErrorClass : inputValidClass}`}
            />
            {error && <p className="text-xs text-amber-400">{error}</p>}
          </div>
        );
      })}
    </div>
  );
}
